// Typed inputs for a category's active `ticket_field_defs` -- shared by
// NewTicketDialog.jsx (creating a ticket) and TicketDetail.jsx (editing
// one), so the five FIELD_TYPES render identically in both places and a
// fix to one does not drift from the other. Presentation only: `defs`
// (from GET /ticket-field-defs/for-category) and `values` (the current
// `custom` map) are owned by the caller, which also decides what to do
// with a changed value -- this component only reports `onChange(key, val)`.
//
// Must match backend/ticket_fields.py's FIELD_TYPES and `_coerce` exactly
// -- the server is the real gate (a bad value is refused 422, surfaced by
// the caller through formatApiError), this only picks the right control
// and passes through a value shape `_coerce` already accepts (a `number`
// field's raw string, for instance -- `_coerce` parses that itself).
const inputCls =
  "w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-blue-500/60 disabled:opacity-50";

export default function CustomFieldInputs({ defs, values, onChange, disabled }) {
  if (!defs?.length) return null;
  return (
    <div className="space-y-3">
      {defs.map((d) => {
        const val = values[d.key];
        const label = (
          <span className="text-xs text-zinc-400">
            {d.label}
            {d.required_on_close && (
              <span className="text-amber-400 ml-1" title="Required before a ticket in this category can close">*</span>
            )}
          </span>
        );
        if (d.type === "checkbox") {
          return (
            <label key={d.key} className="flex items-center gap-2 text-sm text-zinc-300 select-none">
              <input type="checkbox" checked={!!val} disabled={disabled}
                data-testid={`custom-field-${d.key}`}
                onChange={(e) => onChange(d.key, e.target.checked)} />
              {d.label}
              {d.required_on_close && <span className="text-amber-400" title="Required before a ticket in this category can close">*</span>}
            </label>
          );
        }
        if (d.type === "select") {
          return (
            <label key={d.key} className="block">
              {label}
              <select value={val ?? ""} disabled={disabled} data-testid={`custom-field-${d.key}`}
                onChange={(e) => onChange(d.key, e.target.value)}
                className={inputCls}>
                <option value="">— choose —</option>
                {(d.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
          );
        }
        if (d.type === "date") {
          return (
            <label key={d.key} className="block">
              {label}
              <input type="date" value={val ?? ""} disabled={disabled} data-testid={`custom-field-${d.key}`}
                onChange={(e) => onChange(d.key, e.target.value)} className={inputCls} />
            </label>
          );
        }
        if (d.type === "number") {
          return (
            <label key={d.key} className="block">
              {label}
              <input type="number" value={val ?? ""} disabled={disabled} data-testid={`custom-field-${d.key}`}
                onChange={(e) => onChange(d.key, e.target.value)} className={inputCls} />
            </label>
          );
        }
        // "text" and any unrecognised type -- a free-text fallback rather
        // than silently rendering nothing for a type this build does not
        // yet know about.
        return (
          <label key={d.key} className="block">
            {label}
            <input type="text" value={val ?? ""} disabled={disabled} data-testid={`custom-field-${d.key}`}
              onChange={(e) => onChange(d.key, e.target.value)} className={inputCls} />
          </label>
        );
      })}
    </div>
  );
}
